/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {OAuthErrorEvent, OAuthService} from 'angular-oauth2-oidc';
import {User} from '../../shared/model/user';
import {EnvironmentService} from '../environment.service';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    /**
     * A subject to keep track of whether we've saved the Oauth token.
     */
    public static tokenSaved = new BehaviorSubject<boolean>(false);

    constructor(private readonly oAuthService: OAuthService,
                private readonly environmentService: EnvironmentService) {
    }

    /**
     * Observable which will return the user's information from the token
     *
     * This is used to populate a basic user object with the default oauth claims
     * provided by the oauth flow.
     * we fill in the rest of the permissions from the backend later.
     * Use UserService if you need a full CPUser object.
     * @returns basic user object created from identity claims
     */
    get userInfo(): User {
        const claims = this.oAuthService.getIdentityClaims();
        return this.constructUserFromIdentityClaims(claims);
    }

    /**
     * The start of the OAuth Flow.
     * Configures the OAuth client with variables from the current environment,
     * Subscribes to the token recieved event,
     * Calls loadDiscoveryDocumentAndLogin() which will perform the login, and returns a
     * promise if the authentication was successful.
     *
     * We then chain off this promise, and return our own, new Promise if we've saved the token yet.
     */
    async initialize(): Promise<boolean> {
        this.oAuthService.configure(this.environmentService.environment.auth);
        this.tokenSubscription();
        return this.oAuthService.loadDiscoveryDocumentAndLogin()
            .then(() => {
                return this.haveToken();
            });
    }

    /**
     * Returns a Promise that resolves if we have saved the token to Session Storage.
     * Additionally emits to AuthenticationService.tokenSaved that we have a valid token.
     */
    async haveToken(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.oAuthService.hasValidAccessToken()) {
                AuthenticationService.tokenSaved.next(true);
                resolve(true);
            }
        });
    }

    /**
     * Subscribes to the oAuth event for receiving and storing the JWT Token to Session storage.
     * Updates AuthenticationService.tokenSaved when the client saves the token.
     * Handles token refresh failures (e.g. AADSTS700084 - expired SPA refresh token) by logging out
     * and redirecting to login, since Azure AD SPA refresh tokens cannot be extended.
     */
    tokenSubscription() {
        return this.oAuthService.events.subscribe(event => {
            if (event.type === 'token_received') {
                AuthenticationService.tokenSaved.next(true);
            }
            if (event instanceof OAuthErrorEvent &&
                (event.type === 'token_refresh_error' || event.type === 'silent_refresh_error')) {
                this.oAuthService.logOut();
            }
        });
    }

    /**
     * Removes all tokens and logs the user out. If a logout url is configured,
     * the user is redirected to it.
     */
    logout(): void {
        this.oAuthService.logOut();
    }

    /**
     * Will set up silent refreshing for when the token is about to expire.
     * When the user is logged out via this.logOut method, the silent refreshing will pause and not refresh the tokens
     * until the user is logged back in via receiving a new token.
     */
    enableSilentRenew(): void {
        this.oAuthService.setupAutomaticSilentRefresh();
    }

    /**
     * Creates a User from the claims object passed in.
     *
     * @param claims - Object consisting of the user's properties from the OAuth client
     * @returns user object created from identity claims
     */
    private constructUserFromIdentityClaims(claims: any): User {
        let user: User = new User();
        if (claims) {
            user = new User(claims.givenName,
                claims.sn,
                claims.gmuniqueidentifier,
                claims.uid,
                claims.mail,
                claims.roles,
            );
        }
        return user;
    }

}
